/**
 * The wire shape of a tip save (W5): every tip editor sends its list through
 * this, with `idAware: true` beside it when the list came from the author's
 * own read.
 *
 * A tip the editor loaded keeps its `id`, so the server edits THAT row in
 * place — its moderation state goes with it (an edit never lifts a decision,
 * policy §12.3) — and a stale text match can't claim an approved duplicate.
 * A new tip has no id. Client-only fields (`moderationStatus`) stay home.
 *
 * `idAware` tells the server the editor showed every tip it holds, limited
 * and restricted ones included, so a tip missing from the list was deleted
 * by its author (policy §3 lets them). Only say so when the list really came
 * from the author's read: an editor that failed to load, or was typed into
 * before its load landed, sends without it, and the server then keeps the
 * moderated tips it can't tell were deleted
 * (server/services/visitorTipsService.js, applyTipSet).
 *
 * Not an array in, not an array out: callers use "no tips array" to mean
 * "don't touch the tips" (the review batch), so that must survive.
 */
export function tipsForSave(tips) {
  if (!Array.isArray(tips)) return tips;
  return tips.map((tip) => {
    const out = { category: tip?.category, text: tip?.text };
    if (Number.isInteger(tip?.id) && tip.id > 0) out.id = tip.id;
    return out;
  });
}

/** How many tips a save response says were not kept (0 when it doesn't say). */
export function droppedCount(result) {
  const n = Number(result?.dropped);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * What an editor's dropped note says (C359(3)): the dropped tips' own words
 * when the client can name every one, else just how many. `sent` is the list
 * the save sent, as the client holds it; the names come from there, never
 * from the server. A drop the merge can't account for (an edit into a
 * hidden tip's words keeps its row, so nothing went missing) leaves the count.
 *
 * Returns a string[] (one entry per dropped tip) or a number (0 = nothing
 * dropped); ActivityTipsEditor's `dropped` takes either.
 */
export function namedDrops(sent, saved, dropped) {
  const n = Number(dropped) > 0 ? Number(dropped) : 0;
  if (n === 0) return 0;
  const merged = mergeSavedTips(sent, saved, n);
  const names = merged?.droppedTips?.map(tip => String(tip.text).trim()).filter(Boolean);
  return names && names.length === n ? names : n;
}

/**
 * Several editors' `dropped` values (namedDrops) as one, for a surface that
 * reports a whole save at once: every name when each value named its tips,
 * else the total count (0 when nothing was dropped).
 */
export function combineDrops(values) {
  const list = (values || []).filter(v => (Array.isArray(v) ? v.length > 0 : Number(v) > 0));
  if (list.length === 0) return 0;
  if (list.every(Array.isArray)) return list.flat();
  return list.reduce((n, v) => n + (Array.isArray(v) ? v.length : Number(v)), 0);
}

const tipKey = (text) => (typeof text === 'string' ? text.trim().replace(/\s+/g, ' ').toLowerCase() : '');

/**
 * One stop's tips as a review form restores them from its draft (C372): the
 * server's tips as they are, then the draft's tips that aren't among them —
 * the way VisitConfirmationModal folds typing behind a late read. A draft tip
 * carrying an id is a tip that was saved: if the server still holds it, the
 * server's copy stands; if not, it was deleted or taken down since and stays
 * out (re-adding it would bring back what its author or a moderator removed).
 * A draft tip without an id joins unless its text (trimmed, case and spacing
 * aside) is already there. Never drops a server tip, never repeats one; the
 * list stays whole, so the next save may be id-aware.
 */
export function mergeDraftTips(serverTips, draftTips) {
  const server = Array.isArray(serverTips) ? serverTips : [];
  if (!Array.isArray(draftTips) || draftTips.length === 0) return server;
  const seen = new Set(server.map(tip => tipKey(tip?.text)).filter(Boolean));
  const extra = [];
  for (const tip of draftTips) {
    const key = tipKey(tip?.text);
    if (!key || Number.isInteger(tip?.id) || seen.has(key)) continue;
    seen.add(key);
    extra.push({ category: tip.category, text: tip.text });
  }
  return extra.length > 0 ? [...server, ...extra] : server;
}

/**
 * Fold a save response's saved set back into the editor's own list (round 27
 * self-review D4), without moving anything under the user's cursor:
 *   - each local row takes its saved twin's `id` and `moderationStatus` (by
 *     id, else by text), so a new tip's next edit updates that row and keeps
 *     its moderation state (policy §12.3); the row keeps its own text
 *     (a trailing space being typed survives) and its place;
 *   - an empty row stays (the user is rewriting it), losing an id the server
 *     no longer holds;
 *   - a row the server didn't keep goes — but only when exactly as many rows
 *     went unmatched as the response says it dropped; any other answer
 *     (an error read back as an empty set, a merge we can't account for) is
 *     not trusted and returns null, so the caller keeps its list as it is;
 *   - a saved tip the list never had (an unseen moderated tip, after a save
 *     that wasn't id-aware) is NOT inserted: `complete` says whether the list
 *     now holds the whole saved set, i.e. whether the next save may be
 *     id-aware.
 * `droppedTips` are the local rows that went (namedDrops names them).
 */
export function mergeSavedTips(local, saved, dropped = 0) {
  if (!Array.isArray(local) || !Array.isArray(saved)) return null;
  const unused = new Map(saved.map((tip, i) => [i, tip]));
  const take = (match) => {
    for (const [i, tip] of unused) {
      if (match(tip)) { unused.delete(i); return tip; }
    }
    return null;
  };
  const droppedTips = [];
  const tips = [];
  for (const row of local) {
    if (!tipKey(row?.text)) {
      // Being rewritten. It keeps its id only while the server still holds
      // that tip; the save deleted an emptied tip, and a retype under its old
      // id would be dropped as stale.
      if (Number.isInteger(row?.id) && !take(tip => tip.id === row.id)) {
        const { id, moderationStatus, ...rest } = row;
        tips.push(rest);
      } else {
        tips.push(row);
      }
      continue;
    }
    const twin = (Number.isInteger(row.id) && take(tip => tip.id === row.id))
      || take(tip => !Number.isInteger(row.id) && tipKey(tip.text) === tipKey(row.text));
    if (!twin) { droppedTips.push(row); continue; }
    const next = { ...row, id: twin.id };
    if (twin.moderationStatus) next.moderationStatus = twin.moderationStatus;
    else delete next.moderationStatus;
    tips.push(next);
  }
  if (droppedTips.length !== dropped) return null;
  return { tips, complete: unused.size === 0, droppedTips };
}
